import type {
  OpencodeMessage,
  OpencodeMessagePart,
  SessionMessageWithParts,
} from "schemas/generated/opencode";

import React, { createContext } from "react";

import { useRequiredContext } from "replo-utils/react/context";
import { useShallowStore } from "replo-utils/react/zustand";
import { createStore } from "zustand";

interface SessionStore {
  // Per-session event timeline used by chat UIs to render streamed history.
  // Use this for read-only rendering state, not as a source of truth for persisted data.
  events: Record<string, unknown[]>;
  // Per-session messages keyed by upstream opencode message id.
  sessionMessages: Record<string, Record<string, OpencodeMessage>>;
  // Per-session message parts keyed by upstream message id and part opencode id.
  sessionMessageParts: Record<
    string,
    Record<string, Record<string, OpencodeMessagePart>>
  >;
  // NOTE (Kevin, 2026-03-01, REPL-24579): Accumulated text from `message.part.delta`
  // events. Keyed as sessionUuid → messageOpencodeId → partOpencodeId → accumulated text.
  // Deltas are overlaid on top of existing part data during rendering and cleared
  // when the final `message.part.updated` arrives with the complete text.
  streamingTextDeltas: Record<string, Record<string, Record<string, string>>>;
  // Per-session streaming flag for in-progress assistant turns.
  // Use this to control loading states/spinners while a stream is active.
  streamingStates: Record<string, { isStreaming: boolean }>;
  // NOTE (Max, 2026-02-18): Maps toolCallId → questionId for the question/answer flow.
  // Populated when a "question.asked" event arrives so the frontend can
  // reply with the correct questionId when the user picks an answer.
  toolCallIdToQuestionId: Record<string, string>;
  // Frontend session UUID -> backend/agent session id mapping.
  // Use when sending follow-up requests that require the server session id.
  sessionUuidToAgentSessionId: Record<string, string>;
  // Reverse mapping for incoming events that only contain agent session id.
  // Use when routing server events back to the correct frontend session UUID.
  agentSessionIdToSessionUuid: Record<string, string>;
  // Tracks subagent opencode session IDs per parent session UUID, along with
  // their completion state. Used to group subagent messages into collapsible
  // folds in the chat UI.
  subagentSessions: Record<
    string,
    { agentSessionId: string; isComplete: boolean }[]
  >;
  // NOTE (Berkley, 2026-03-04, REPL-24580): Maps a Task tool call to the
  // subagent session it spawned. Needed because a single parent session can
  // spawn multiple subagents and we need each SubagentTaskToolRow to display
  // only its own subagent's messages rather than all subagent messages.
  toolCallIdToSubagentSessionId: Record<string, string>;
  // NOTE (Ben, 2026-03-09, REPL-24858): Optimistic messages shown immediately on
  // send before the backend round-trips real messages. Contains both a user message
  // (with text) and an assistant message (empty parts, renders typing indicator).
  // Individual entries are cleared as their real counterparts arrive; all entries
  // are cleared when streaming stops.
  optimisticMessages: Record<string, SessionMessageWithParts[]>;
  actions: {
    // Append a new event as it arrives for an existing session timeline.
    addEvent: ({
      sessionUuid,
      event,
    }: {
      sessionUuid: string;
      event: unknown;
    }) => void;
    addOrUpdateMessage: ({
      sessionUuid,
      message,
    }: {
      sessionUuid: string;
      message: OpencodeMessage;
    }) => void;
    addOrUpdateMessagePart: ({
      sessionUuid,
      messageOpencodeId,
      part,
    }: {
      sessionUuid: string;
      messageOpencodeId: string;
      part: OpencodeMessagePart;
    }) => void;
    // Set streaming lifecycle state at turn boundaries (start/end/error).
    setStreaming: ({
      sessionUuid,
      isStreaming,
    }: {
      sessionUuid: string;
      isStreaming: boolean;
    }) => void;
    // Register session ids after session creation/lookup so future events can be routed.
    registerSession: ({
      sessionUuid,
      agentSessionId,
    }: {
      sessionUuid: string;
      agentSessionId: string;
    }) => void;
    getMessageByOpencodeId: ({
      sessionUuid,
      messageOpencodeId,
    }: {
      sessionUuid: string;
      messageOpencodeId: string;
    }) => OpencodeMessage | undefined;
    getAgentSessionId: ({
      sessionUuid,
    }: {
      sessionUuid: string;
    }) => string | undefined;
    getSessionUuidByAgentSessionId: ({
      agentSessionId,
    }: {
      agentSessionId: string;
    }) => string | undefined;
    setQuestionId: ({
      toolCallId,
      questionId,
    }: {
      toolCallId: string;
      questionId: string;
    }) => void;
    getQuestionId: ({
      toolCallId,
    }: {
      toolCallId: string;
    }) => string | undefined;
    // NOTE (Kevin, 2026-03-01, REPL-24579): Accumulate incremental text from
    // `message.part.delta` events for real-time streaming display.
    applyTextDelta: ({
      sessionUuid,
      messageOpencodeId,
      partOpencodeId,
      delta,
    }: {
      sessionUuid: string;
      messageOpencodeId: string;
      partOpencodeId: string;
      delta: string;
    }) => void;
    // NOTE (Berkley, 2026-03-04, REPL-24580): Only sets the reverse mapping
    // (agentSessionId → sessionUuid) so subagent events route to the parent's
    // session without overwriting the parent's forward mapping used by
    // getAgentSessionId for sending prompts.
    registerSubagentAgentSessionId: ({
      parentSessionUuid,
      agentSessionId,
    }: {
      parentSessionUuid: string;
      agentSessionId: string;
    }) => void;
    registerSubagentSession: ({
      parentSessionUuid,
      subagentAgentSessionId,
    }: {
      parentSessionUuid: string;
      subagentAgentSessionId: string;
    }) => void;
    completeSubagentSession: ({
      agentSessionId,
    }: {
      agentSessionId: string;
    }) => void;
    setToolCallSubagentSession: ({
      toolCallId,
      agentSessionId,
    }: {
      toolCallId: string;
      agentSessionId: string;
    }) => void;
    setOptimisticMessages: ({
      sessionUuid,
      messages,
    }: {
      sessionUuid: string;
      messages: SessionMessageWithParts[];
    }) => void;
  };
  queries: {
    isSubagentSession: ({
      agentSessionId,
    }: {
      agentSessionId: string;
    }) => boolean;
    findRunningTaskToolCallId: ({
      sessionUuid,
    }: {
      sessionUuid: string;
    }) => string | undefined;
    hasActiveSubagents: ({ sessionUuid }: { sessionUuid: string }) => boolean;
  };
}

const createSessionStore = () => {
  return createStore<SessionStore>((set, get) => ({
    events: {},
    sessionMessages: {},
    sessionMessageParts: {},
    streamingTextDeltas: {},
    streamingStates: {},
    toolCallIdToQuestionId: {},
    sessionUuidToAgentSessionId: {},
    agentSessionIdToSessionUuid: {},
    subagentSessions: {},
    toolCallIdToSubagentSessionId: {},
    optimisticMessages: {},
    actions: {
      addEvent: ({ sessionUuid, event }) => {
        set((state) => ({
          events: {
            ...state.events,
            [sessionUuid]: [...(state.events[sessionUuid] ?? []), event],
          },
        }));
      },
      addOrUpdateMessage: ({ sessionUuid, message }) => {
        set((state) => {
          const existingMessage =
            state.sessionMessages[sessionUuid]?.[message.opencodeId];

          // NOTE (Max, 2026-02-18): Skip if the existing message is newer (avoids historical fetch
          // overwriting a more up-to-date real-time event).
          if (
            existingMessage &&
            existingMessage.updatedAt >= message.updatedAt
          ) {
            return state;
          }

          const nextMessage = existingMessage
            ? {
                ...message,
                id: existingMessage.id,
                createdAt: existingMessage.createdAt,
              }
            : message;

          return {
            sessionMessages: {
              ...state.sessionMessages,
              [sessionUuid]: {
                ...(state.sessionMessages[sessionUuid] ?? {}),
                [message.opencodeId]: nextMessage,
              },
            },
            // NOTE (Ben, 2026-03-09, REPL-24858): Clear the optimistic assistant
            // message when the real assistant message arrives from the backend.
            ...(message.role === "assistant"
              ? {
                  optimisticMessages: {
                    ...state.optimisticMessages,
                    [sessionUuid]: (
                      state.optimisticMessages[sessionUuid] ?? []
                    ).filter((m) => m.message.role !== "assistant"),
                  },
                }
              : {}),
          };
        });
      },
      addOrUpdateMessagePart: ({ sessionUuid, messageOpencodeId, part }) => {
        set((state) => {
          const existingPart =
            state.sessionMessageParts[sessionUuid]?.[messageOpencodeId]?.[
              part.opencodeId
            ];

          // NOTE (Max, 2026-02-18): Skip if the existing part is newer (avoids historical fetch
          // overwriting a more up-to-date real-time event).
          if (existingPart && existingPart.updatedAt >= part.updatedAt) {
            return state;
          }

          const nextPart = existingPart
            ? {
                ...part,
                id: existingPart.id,
                createdAt: existingPart.createdAt,
              }
            : part;

          // NOTE (Kevin, 2026-03-01, REPL-24579): When the final part data arrives,
          // clear any accumulated streaming deltas for this part since the persisted
          // data is now the source of truth.
          const messageDeltaEntries =
            state.streamingTextDeltas[sessionUuid]?.[messageOpencodeId];
          let nextStreamingTextDeltas = state.streamingTextDeltas;
          if (messageDeltaEntries?.[part.opencodeId]) {
            const { [part.opencodeId]: _, ...remainingPartDeltas } =
              messageDeltaEntries;
            nextStreamingTextDeltas = {
              ...state.streamingTextDeltas,
              [sessionUuid]: {
                ...(state.streamingTextDeltas[sessionUuid] ?? {}),
                [messageOpencodeId]: remainingPartDeltas,
              },
            };
          }

          // NOTE (Ben, 2026-03-09, REPL-24858): Clear the optimistic user message
          // when the real part arrives (not on message.created) so the optimistic
          // text stays visible until the real text part is ready, avoiding layout shift.
          const parentMessage =
            state.sessionMessages[sessionUuid]?.[messageOpencodeId];
          const shouldClearOptimistic = parentMessage?.role === "user";

          return {
            sessionMessageParts: {
              ...state.sessionMessageParts,
              [sessionUuid]: {
                ...(state.sessionMessageParts[sessionUuid] ?? {}),
                [messageOpencodeId]: {
                  ...(state.sessionMessageParts[sessionUuid]?.[
                    messageOpencodeId
                  ] ?? {}),
                  [part.opencodeId]: nextPart,
                },
              },
            },
            streamingTextDeltas: nextStreamingTextDeltas,
            ...(shouldClearOptimistic
              ? {
                  optimisticMessages: {
                    ...state.optimisticMessages,
                    [sessionUuid]: (
                      state.optimisticMessages[sessionUuid] ?? []
                    ).filter((m) => m.message.role !== "user"),
                  },
                }
              : {}),
          };
        });
      },
      setStreaming: ({ sessionUuid, isStreaming }) => {
        set((state) => {
          const { [sessionUuid]: _, ...remainingDeltas } =
            state.streamingTextDeltas;
          return {
            streamingStates: {
              ...state.streamingStates,
              [sessionUuid]: { isStreaming },
            },
            ...(!isStreaming
              ? {
                  streamingTextDeltas: remainingDeltas,
                  optimisticMessages: {
                    ...state.optimisticMessages,
                    [sessionUuid]: [],
                  },
                }
              : {}),
          };
        });
      },
      registerSession: ({ sessionUuid, agentSessionId }) => {
        set((state) => ({
          sessionUuidToAgentSessionId: {
            ...state.sessionUuidToAgentSessionId,
            [sessionUuid]: agentSessionId,
          },
          agentSessionIdToSessionUuid: {
            ...state.agentSessionIdToSessionUuid,
            [agentSessionId]: sessionUuid,
          },
        }));
      },
      getMessageByOpencodeId: ({ sessionUuid, messageOpencodeId }) => {
        return get().sessionMessages[sessionUuid]?.[messageOpencodeId];
      },
      getAgentSessionId: ({ sessionUuid }) => {
        return get().sessionUuidToAgentSessionId[sessionUuid];
      },
      getSessionUuidByAgentSessionId: ({ agentSessionId }) => {
        return get().agentSessionIdToSessionUuid[agentSessionId];
      },
      setQuestionId: ({ toolCallId, questionId }) => {
        set((state) => ({
          toolCallIdToQuestionId: {
            ...state.toolCallIdToQuestionId,
            [toolCallId]: questionId,
          },
        }));
      },
      getQuestionId: ({ toolCallId }) => {
        return get().toolCallIdToQuestionId[toolCallId];
      },
      applyTextDelta: ({
        sessionUuid,
        messageOpencodeId,
        partOpencodeId,
        delta,
      }) => {
        set((state) => {
          const existing =
            state.streamingTextDeltas[sessionUuid]?.[messageOpencodeId]?.[
              partOpencodeId
            ] ?? "";

          return {
            streamingTextDeltas: {
              ...state.streamingTextDeltas,
              [sessionUuid]: {
                ...(state.streamingTextDeltas[sessionUuid] ?? {}),
                [messageOpencodeId]: {
                  ...(state.streamingTextDeltas[sessionUuid]?.[
                    messageOpencodeId
                  ] ?? {}),
                  [partOpencodeId]: existing + delta,
                },
              },
            },
          };
        });
      },
      registerSubagentAgentSessionId: ({
        parentSessionUuid,
        agentSessionId,
      }) => {
        set((state) => ({
          agentSessionIdToSessionUuid: {
            ...state.agentSessionIdToSessionUuid,
            [agentSessionId]: parentSessionUuid,
          },
        }));
      },
      registerSubagentSession: ({
        parentSessionUuid,
        subagentAgentSessionId,
      }) => {
        set((state) => ({
          subagentSessions: {
            ...state.subagentSessions,
            [parentSessionUuid]: [
              ...(state.subagentSessions[parentSessionUuid] ?? []),
              { agentSessionId: subagentAgentSessionId, isComplete: false },
            ],
          },
        }));
      },
      completeSubagentSession: ({ agentSessionId }) => {
        set((state) => {
          const updated = { ...state.subagentSessions };
          for (const [parentUuid, subs] of Object.entries(updated)) {
            const idx = subs.findIndex(
              (s) => s.agentSessionId === agentSessionId,
            );
            if (idx !== -1) {
              updated[parentUuid] = subs.map((s, i) => {
                return i === idx ? { ...s, isComplete: true } : s;
              });
              break;
            }
          }
          return { subagentSessions: updated };
        });
      },
      setToolCallSubagentSession: ({ toolCallId, agentSessionId }) => {
        set((state) => ({
          toolCallIdToSubagentSessionId: {
            ...state.toolCallIdToSubagentSessionId,
            [toolCallId]: agentSessionId,
          },
        }));
      },
      setOptimisticMessages: ({ sessionUuid, messages }) => {
        set((state) => ({
          optimisticMessages: {
            ...state.optimisticMessages,
            [sessionUuid]: messages,
          },
        }));
      },
    },
    queries: {
      isSubagentSession: ({ agentSessionId }) => {
        const subs = get().subagentSessions;
        return Object.values(subs).some((entries) => {
          return entries.some((e) => e.agentSessionId === agentSessionId);
        });
      },
      findRunningTaskToolCallId: ({ sessionUuid }) => {
        const state = get();
        const allParts = Object.values(
          state.sessionMessageParts[sessionUuid] ?? {},
        ).flatMap((messageParts) => Object.values(messageParts));
        const mapped = state.toolCallIdToSubagentSessionId;
        const runningTaskPart = allParts.find((p) => {
          return (
            p.partData.type === "tool" &&
            p.partData.tool.toLowerCase() === "task" &&
            (p.partData.state.status === "running" ||
              p.partData.state.status === "pending") &&
            !(p.partData.callID in mapped)
          );
        });
        if (runningTaskPart?.partData.type === "tool") {
          return runningTaskPart.partData.callID;
        }
        return undefined;
      },
      hasActiveSubagents: ({ sessionUuid }) => {
        const subs = get().subagentSessions[sessionUuid] ?? [];
        return subs.some((s) => !s.isComplete);
      },
    },
  }));
};

type SessionContextType = {
  store: ReturnType<typeof createSessionStore>;
};

const SessionContext = createContext<SessionContextType | null>(null);

const useSessionContext = () => {
  return useRequiredContext(SessionContext);
};

export function SessionProvider({ children }: React.PropsWithChildren) {
  // Keep one stable store instance for the lifetime of this provider tree.
  const [store] = React.useState(() => createSessionStore());

  const contextValue = React.useMemo(() => ({ store }), [store]);

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  );
}

function useSessionStore<SelectorReturnType>(
  selector: (state: SessionStore) => SelectorReturnType,
): SelectorReturnType {
  const { store } = useSessionContext();
  return useShallowStore(store, selector);
}

export const useSessionStoreActions = () => {
  return useSessionStore((state) => state.actions);
};

export const useSessionStoreQueries = () => {
  return useSessionStore((state) => state.queries);
};

export const useMessagesForSession = (sessionUuid: string | null) => {
  return useSessionStore((state): SessionMessageWithParts[] => {
    if (!sessionUuid) {
      return [];
    }

    const sessionMessages = Object.values(
      state.sessionMessages[sessionUuid] ?? {},
    ).sort((firstMessage, secondMessage) => {
      return (
        firstMessage.createdAt.getTime() - secondMessage.createdAt.getTime()
      );
    });

    const sessionDeltas = state.streamingTextDeltas[sessionUuid];

    const result: SessionMessageWithParts[] = sessionMessages.map((message) => {
      const rawParts = Object.values(
        state.sessionMessageParts[sessionUuid]?.[message.opencodeId] ?? {},
      ).sort((firstPart, secondPart) => {
        return firstPart.createdAt.getTime() - secondPart.createdAt.getTime();
      });

      const messageDeltas = sessionDeltas?.[message.opencodeId];
      const parts = messageDeltas
        ? rawParts.map((part) => {
            const deltaText = messageDeltas[part.opencodeId];
            if (deltaText !== undefined && part.partData.type === "text") {
              return {
                ...part,
                partData: {
                  ...part.partData,
                  text: part.partData.text + deltaText,
                },
              };
            }
            return part;
          })
        : rawParts;

      return { message, parts };
    });

    // NOTE (Ben, 2026-03-09, REPL-24858): Append optimistic messages so they appear
    // instantly while the backend round-trips. Individual entries are removed as
    // their real counterparts arrive; all are cleared when streaming stops.
    const optimisticMessages = state.optimisticMessages[sessionUuid];
    if (optimisticMessages) {
      result.push(...optimisticMessages);
    }

    return result;
  });
};

export const useStreamingSessions = () => {
  return useSessionStore((state) => {
    return Object.entries(state.streamingStates)
      .filter(([, streamingState]) => streamingState.isStreaming)
      .map(([sessionUuid]) => sessionUuid);
  });
};

export const useIsSessionStreaming = (sessionUuid: string | null) => {
  // Use for UI loading indicators tied to a session's active assistant turn.
  return useSessionStore((state) => {
    if (!sessionUuid) {
      return false;
    }
    return state.streamingStates[sessionUuid]?.isStreaming ?? false;
  });
};

export const useToolCallSubagentSessionId = (toolCallId: string) => {
  return useSessionStore(
    (state) => state.toolCallIdToSubagentSessionId[toolCallId] ?? null,
  );
};

export const useSubagentSessions = (sessionUuid: string | null) => {
  return useSessionStore((state) => {
    if (!sessionUuid) {
      return [];
    }
    return state.subagentSessions[sessionUuid] ?? [];
  });
};
